import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RegistryCategory, ExplorerItem, ViewMode } from './types';
import { CompactRow } from './CompactRow';
import { Card } from '../../../../../components/ui/Card';
interface CategorySectionProps {
  category: RegistryCategory;
  items: ExplorerItem[];
  viewMode: ViewMode;
  onSelectItem: (id: string) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  accentColor?: 'blue' | 'purple';
  renderCardContent?: (item: ExplorerItem) => React.ReactNode;
}
export function CategorySection({
  category,
  items,
  viewMode,
  onSelectItem,
  isExpanded,
  onToggleExpanded,
  accentColor = 'blue',
  renderCardContent
}: CategorySectionProps) {
  const Icon = category.icon;
  if (items.length === 0) return null;
  const activeCount = items.filter(i => i.usageCount > 0).length;
  const accentClass = accentColor === 'blue' ? 'text-blue-600' : 'text-purple-600';
  const bgAccentClass = accentColor === 'blue' ? 'bg-blue-50' : 'bg-purple-50';
  return <div className="mb-4 sm:mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6294" data-mp="27051hxa2eb0n">
      <button onClick={onToggleExpanded} className="w-full px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between bg-gray-50/50 hover:bg-gray-50 transition-colors border-b border-gray-100 gap-3" data-id="element-6295" data-mp="220ilxh6rkioo">
        
        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1" data-id="element-6296" data-mp="2upza22ncpdf3">
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 ${bgAccentClass} ${accentClass}`} data-id="element-6297" data-mp="2pqcxlvfck17c">
            
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-6298" data-mp="2lngf04pt453e" />
          </div>
          <div className="text-left min-w-0" data-id="element-6299" data-mp="2gqhk3jybju45">
            <h4 className="font-bold text-sm sm:text-base text-gray-900 truncate" data-id="element-6300" data-mp="2grsmpgg12vto">
              {category.label}
            </h4>
            <p className="text-[11px] sm:text-xs text-gray-500 truncate" data-id="element-6301" data-mp="15aswqrh5kt2t">
              <span className="sm:hidden" data-id="element-6302" data-mp="15cm1g187h386">
                {activeCount}/{items.length} actief
              </span>
              <span className="hidden sm:inline" data-id="element-6303" data-mp="1bqrmjr4t82hy">{category.description}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0" data-id="element-6304" data-mp="xrjncy8ncudr">
          <div className="text-right hidden sm:block" data-id="element-6305" data-mp="xtcs27zp94j4">
            <div className="text-sm font-medium text-gray-900" data-id="element-6306" data-mp="11raitf94byom">
              {activeCount} / {items.length} actief
            </div>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" data-id="element-6307" data-mp="srnkj2bor8zs" /> : <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" data-id="element-6308" data-mp="su0ao2rlkpnx" />}
        </div>
      </button>

      <AnimatePresence initial={false} data-id="element-6309" data-mp="e0y6ls6ubfs8">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-6310" data-mp="j35k4m7bj3wo">
          
            <div className="p-3 sm:p-5" data-id="element-6311" data-mp="3mk8hnt2k18ts">
              {viewMode === 'compact' ? <div className="flex flex-col gap-1.5 sm:gap-2" data-id="element-6312" data-mp="3mmuy6i7fayo5">
                  {items.map(item => <CompactRow key={item.id} item={item} onClick={() => onSelectItem(item.id)} accentColor={accentColor} data-id="element-6313" data-mp="3ho1nsynfnns7" />)}
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-id="element-6314" data-mp="3rkcyxlbae7l2">
                  {items.map(item => {
              const ItemIcon = item.icon;
              const isActive = item.usageCount > 0;
              const hoverClass = accentColor === 'blue' ? 'hover:border-blue-300' : 'hover:border-purple-300';
              return <Card key={item.id} className={`p-3 sm:p-4 h-full flex flex-col transition-all duration-200 ${isActive ? `cursor-pointer group relative ${hoverClass} border-gray-200 hover:shadow-md` : 'opacity-50 border-gray-100 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isActive && onSelectItem(item.id)} data-id="element-6315" data-mp="j3i5ewgdpzjn">
                    
                        <div className="flex items-start justify-between mb-2.5 sm:mb-3" data-id="element-6316" data-mp="2hmn0hho4s8or">
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${isActive ? `${bgAccentClass} ${accentClass} group-hover:scale-105 transition-transform` : 'bg-gray-200 text-gray-400'}`} data-id="element-6317" data-mp="2mkycnz1weva5">
                        
                            <ItemIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6318" data-mp="2vcqexmgrqy3n" />
                          </div>
                          <div className="text-right" data-id="element-6319" data-mp="1ypc3k3pz1rch">
                            <div className={`text-base sm:text-lg font-black ${isActive ? accentClass : 'text-gray-400'}`} data-id="element-6320" data-mp="23ndkoj3u7glb">
                          
                              {item.usageCount}
                            </div>
                            <div className="text-[10px] text-gray-500 font-medium" data-id="element-6321" data-mp="2cobslchec896">
                              refs
                            </div>
                          </div>
                        </div>

                        <div className="mb-2.5 sm:mb-3 min-w-0" data-id="element-6322" data-mp="3o4a2fikeeybg">
                          <div className="flex items-center gap-2 mb-1 min-w-0" data-id="element-6323" data-mp="1zezargwld4i">
                            <code className="text-xs sm:text-sm font-mono font-bold text-gray-900 truncate" data-id="element-6324" data-mp="3t6rifc1ck0n2">
                              {item.id}
                            </code>
                            {!item.isRegistered && <span className="text-[8px] bg-red-100 text-red-700 px-1 py-0.5 rounded font-bold uppercase tracking-wider shrink-0" data-id="element-6325" data-mp="3kdo8vetto7xp">
                                Unreg
                              </span>}
                          </div>
                          <p className="text-[11px] sm:text-xs text-gray-500 line-clamp-2" title={item.description} data-id="element-6326" data-mp="35hgmq8yuswfs">
                        
                            {item.description}
                          </p>
                        </div>

                        {renderCardContent && isActive && <div className="mt-auto pt-2.5 sm:pt-3 border-t border-gray-100" data-id="element-6327" data-mp="13nzg98mgm1mm">
                            {renderCardContent(item)}
                          </div>}

                        {!renderCardContent && isActive && <div className="mt-auto pt-2.5 sm:pt-3 border-t border-gray-100" data-id="element-6328" data-mp="2t5ebbzm9k3ic">
                            <div className="flex flex-wrap gap-1" data-id="element-6329" data-mp="2xzs9g9sb8oqp">
                              {Array.from(item.modules).sort().slice(0, 3).map(m => <span key={m} className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-gray-50 border-gray-200 text-gray-600" data-id="element-6330" data-mp="2o7ut6omg36l4">
                          
                                    {m}
                                  </span>)}
                              {item.modules.size > 3 && <span className="text-[10px] text-gray-400 font-medium px-1 py-0.5" data-id="element-6331" data-mp="32x0t6et3x0u2">
                                  +{item.modules.size - 3}
                                </span>}
                            </div>
                          </div>}
                      </Card>;
            })}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}